import { API, STATS_API } from "../../constants";
import { ApiResponse } from "../../types";
import { apiSlice } from "../api-slice";
import { GetTeamByIdQueryParams, GetTeamsByLeagueQueryParams, Team } from "./types";

const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getAllTeamNames: build.query<string[], void>({
      query: () => `${API}/team/allactivebyplays`,
      transformResponse: (response: ApiResponse<string[]>) => response.data,
      providesTags: ["Team"],
    }),
    getAllTeams: build.query<Team[], void>({
      query: () => `${API}/team/allactive`,
      transformResponse: (response: ApiResponse<Team[]>) => response.data,
      providesTags: ["Team"],
    }),
    getTeamsByLeague: build.query<Team[], GetTeamsByLeagueQueryParams>({
      query: ({ league }) => `${STATS_API}/query/teams?league=${league}&full=true`,
      providesTags: ["Team"],
    }),
    getTeamById: build.query<Team, GetTeamByIdQueryParams>({
      query: ({ id }) => `${STATS_API}/query/teams/${id}?full=true`,
    }),
    createTeam: build.mutation<ApiResponse<Team>, { name: string }>({
      query: (team) => ({ method: "POST", url: `${API}/team`, body: team }),
      invalidatesTags: ["Team"],
    }),
    deleteTeam: build.mutation<ApiResponse<Team>, { id: string }>({
      query: ({ id }) => ({ method: "DELETE", url: `${API}/team/${id}?deletePlay=true` }),
      invalidatesTags: ["Team"],
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        const result = dispatch(
          extendedApiSlice.util.updateQueryData("getAllTeams", undefined, (draft) => {
            return draft.filter((team) => team.id !== id);
          })
        );

        try {
          await queryFulfilled;
        } catch {
          result.undo();
        }
      },
    }),
    editTeam: build.mutation<ApiResponse<Team>, { id: string; team_id: string; name: string }>({
      query: ({ id, team_id, name }) => ({ method: "POST", url: `${API}/team`, body: { name, id, team_id } }),
      invalidatesTags: ["Team"],
      onQueryStarted: async ({ id, name }, { dispatch, queryFulfilled }) => {
        const result = dispatch(
          extendedApiSlice.util.updateQueryData("getAllTeams", undefined, (draft) => {
            const indexToUpdate = draft.findIndex((team) => team.id === id);
            draft.splice(indexToUpdate, 1, { ...(draft[indexToUpdate] as Team), name });
          })
        );

        try {
          await queryFulfilled;
        } catch {
          result.undo();
        }
      },
    }),
  }),
});

export const {
  useGetAllTeamNamesQuery,
  useLazyGetAllTeamNamesQuery,
  useGetAllTeamsQuery,
  useLazyGetAllTeamsQuery,
  useGetTeamsByLeagueQuery,
  useLazyGetTeamsByLeagueQuery,
  useGetTeamByIdQuery,
  useLazyGetTeamByIdQuery,
  useCreateTeamMutation,
  useDeleteTeamMutation,
  useEditTeamMutation,
} = extendedApiSlice;
